import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Helper: detect the OS / device colour-scheme preference
const getSystemDarkMode = () =>
  window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Migrate: clear the old "theme" key so stale values don't override system pref
    const hadOldKey = localStorage.getItem('theme');
    if (hadOldKey && !localStorage.getItem('theme-user-set')) {
      localStorage.removeItem('theme');
    }

    // Only honour a manually-chosen preference (flagged by "theme-user-set")
    const userExplicitlyChose = localStorage.getItem('theme-user-set') === 'true';
    if (userExplicitlyChose) {
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme) return savedTheme === 'dark';
    }

    // No manual preference — follow the device / OS theme
    return getSystemDarkMode();
  });

  const toggleTheme = () => {
    setIsDarkMode(prev => {
      const newTheme = !prev;
      localStorage.setItem('theme', newTheme ? 'dark' : 'light');
      // Mark that the user explicitly chose a theme
      localStorage.setItem('theme-user-set', 'true');
      return newTheme;
    });
  };

  // Listen for OS-level theme changes in real time
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e) => {
      // Only auto-switch if the user hasn't manually picked a theme
      const userExplicitlyChose = localStorage.getItem('theme-user-set') === 'true';
      if (!userExplicitlyChose) {
        setIsDarkMode(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    // Apply theme class to document root
    if (isDarkMode) {
      document.documentElement.classList.add('dark-theme');
      document.documentElement.classList.remove('light-theme');
    } else {
      document.documentElement.classList.add('light-theme');
      document.documentElement.classList.remove('dark-theme');
    }
  }, [isDarkMode]);

  const value = {
    isDarkMode,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}; 